import {
  TextField,
  Grid,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Typography,
  Chip,
  IconButton,
  InputAdornment,
  Tooltip,
  Button,
} from '@material-ui/core';
import EventBusyIcon from '@material-ui/icons/EventBusy';
import CancelIcon from '@material-ui/icons/Cancel';
import * as React from 'react';
import { FormDialog } from 'components/FormDialog';
import { ReservationService } from 'services';
import { Reservation, ReservationStatus } from 'interfaces';
import { TableAvailabilityDialog } from '../TableAvailabilityDialog';
import { CancelReservationDialog } from '../CancelReservationDialog';

interface Props {
  open: boolean;
  reservation: Reservation | null;
  onClose: () => void;
  onSaved: () => void;
}

const STATUSES: ReservationStatus[] = [
  'requested',
  'approved',
  'declined',
  'canceled',
  'completed',
  'no_show',
];

const toDateTimeLocal = (iso: string) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours(),
  )}:${pad(d.getMinutes())}`;
};

export const EditReservationDialog: React.FC<Props> = ({
  open,
  reservation,
  onClose,
  onSaved,
}) => {
  const [form, setForm] = React.useState({
    reserved_at: '',
    status: 'requested' as ReservationStatus,
    table_names: '',
    duration_minutes: 120,
    party_size: 1,
    notes: '',
    admin_notes: '',
  });
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [availabilityOpen, setAvailabilityOpen] = React.useState(false);
  const [cancelOpen, setCancelOpen] = React.useState(false);

  React.useEffect(() => {
    if (!reservation) return;
    setForm({
      reserved_at: toDateTimeLocal(reservation.reservedAt),
      status: reservation.status,
      table_names: reservation.tableNames || '',
      duration_minutes: reservation.durationMinutes || 120,
      party_size: reservation.partySize,
      notes: reservation.notes || '',
      admin_notes: reservation.adminNotes || '',
    });
    setError(null);
  }, [reservation]);

  if (!reservation) {
    return null;
  }

  const setField = (field: keyof typeof form, value: any) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const payload: any = {
        reserved_at: form.reserved_at.replace('T', ' ') + ':00',
        status: form.status,
        // Send empty string instead of null. URLSearchParams stringifies
        // null to the literal "null" string which then lands in the DB.
        table_names: form.table_names || '',
        duration_minutes: form.duration_minutes,
        party_size: form.party_size,
        notes: form.notes || '',
        admin_notes: form.admin_notes || '',
      };
      await ReservationService.update(reservation.id, payload);
      onSaved();
    } catch (e) {
      setError((e as Error)?.message || 'Failed to update reservation');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <FormDialog
      title={
        <span>
          Reservation #{reservation.externalReference}{' '}
          <Chip size="small" label={reservation.source} />
        </span>
      }
      dialogProps={{
        open,
        onClose,
        fullWidth: true,
        maxWidth: 'md',
      }}
      submitText="Save"
      loading={submitting}
      onSubmit={handleSubmit}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        <Typography variant="caption" color="textSecondary">
          Customer: {reservation.name} · {reservation.email} ·{' '}
          {reservation.phoneNumber}
          {reservation.experience && (
            <>
              {' '}
              · Experience: <strong>{reservation.experience.title}</strong>
            </>
          )}
        </Typography>
        {reservation.status !== 'canceled' &&
          reservation.status !== 'completed' && (
            <Button
              size="small"
              variant="outlined"
              color="secondary"
              startIcon={<CancelIcon />}
              onClick={() => setCancelOpen(true)}
            >
              Cancel reservation
            </Button>
          )}
      </div>

      {error && (
        <Typography color="error" variant="body2" style={{ marginTop: 8 }}>
          {error}
        </Typography>
      )}

      <Grid container spacing={2} style={{ marginTop: 4 }}>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Reserved at"
            type="datetime-local"
            value={form.reserved_at}
            onChange={(e) => setField('reserved_at', e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
            required
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <TextField
            label="Party size"
            type="number"
            inputProps={{ min: 1, max: 50 }}
            value={form.party_size}
            onChange={(e) =>
              setField('party_size', parseInt(e.target.value, 10) || 1)
            }
            fullWidth
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <TextField
            label="Duration (min)"
            type="number"
            inputProps={{ min: 30, step: 15 }}
            value={form.duration_minutes}
            onChange={(e) =>
              setField('duration_minutes', parseInt(e.target.value, 10) || 120)
            }
            fullWidth
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Table (comma-separated)"
            value={form.table_names}
            onChange={(e) => setField('table_names', e.target.value)}
            placeholder="e.g. 22 or 16, 1, 13"
            fullWidth
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip title="See bookings already on this date">
                    <IconButton
                      size="small"
                      onClick={() => setAvailabilityOpen(true)}
                      disabled={!form.reserved_at}
                    >
                      <EventBusyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
              value={form.status}
              onChange={(e) =>
                setField('status', e.target.value as ReservationStatus)
              }
            >
              {STATUSES.map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Customer notes"
            value={form.notes}
            onChange={(e) => setField('notes', e.target.value)}
            multiline
            rows={2}
            fullWidth
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Admin notes"
            value={form.admin_notes}
            onChange={(e) => setField('admin_notes', e.target.value)}
            multiline
            rows={2}
            fullWidth
          />
        </Grid>
      </Grid>

      <TableAvailabilityDialog
        open={availabilityOpen}
        onClose={() => setAvailabilityOpen(false)}
        date={(form.reserved_at || '').slice(0, 10)}
        reservedAt={form.reserved_at}
        durationMinutes={form.duration_minutes}
      />

      <CancelReservationDialog
        open={cancelOpen}
        reservation={reservation}
        onClose={() => setCancelOpen(false)}
        onCanceled={() => {
          setCancelOpen(false);
          onSaved();
        }}
      />
    </FormDialog>
  );
};
